import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

interface PendingImageGeneration {
  postId: string;
  startedAt: Date;
}

interface ImageGenerationContextType {
  pendingImages: Map<string, PendingImageGeneration>;
  startImageGeneration: (postId: string, generate: () => Promise<void>) => void;
  isImagePending: (postId: string) => boolean;
}

const ImageGenerationContext = createContext<ImageGenerationContextType | undefined>(undefined);

export function ImageGenerationProvider({ children }: { children: ReactNode }) {
  const [pendingImages, setPendingImages] = useState<Map<string, PendingImageGeneration>>(new Map());

  const startImageGeneration = useCallback((postId: string, generate: () => Promise<void>) => {
    setPendingImages(prev => {
      const next = new Map(prev);
      next.set(postId, { postId, startedAt: new Date() });
      return next;
    });

    generate().finally(() => {
      setPendingImages(prev => {
        const next = new Map(prev);
        next.delete(postId);
        return next;
      });
    });
  }, []);

  const isImagePending = useCallback((postId: string) => {
    return pendingImages.has(postId);
  }, [pendingImages]);

  const value = useMemo(
    () => ({ pendingImages, startImageGeneration, isImagePending }),
    [pendingImages, startImageGeneration, isImagePending]
  );

  return (
    <ImageGenerationContext.Provider value={value}>
      {children}
    </ImageGenerationContext.Provider>
  );
}

export function useImageGenerationContext() {
  const context = useContext(ImageGenerationContext);
  if (context === undefined) {
    throw new Error('useImageGenerationContext must be used within an ImageGenerationProvider');
  }
  return context;
}
